import React, { useState, useEffect, useMemo } from 'react';

interface OptionChainRow {
  strike: number;
  callOI: number;
  callVolume: number;
  callLTP: number;
  callIV: string;
  callBid: number;
  callAsk: number;
  putOI: number;
  putVolume: number;
  putLTP: number;
  putIV: string;
  putBid: number;
  putAsk: number;
}

interface StrategyLeg {
  id: string;
  type: 'CE' | 'PE';
  action: 'BUY' | 'SELL';
  strike: number;
  lots: number;
  premium: number;
}

interface StrategyTemplate {
  name: string;
  description: string;
  legs: Omit<StrategyLeg, 'id' | 'premium'>[];
  getStrikes: (atmStrike: number, stepSize: number) => number[];
}

interface OILevel {
  strike: number;
  type: 'oi_support' | 'oi_resistance';
  oi: number;
}

interface Props {
  optionChain: OptionChainRow[];
  spotPrice: number;
  recommendedStrategy?: string;
  lotSize?: number;
  oiLevels?: OILevel[];
}

const STRATEGIES: Record<string, StrategyTemplate> = {
  'Long Call': {
    name: 'Long Call',
    description: 'Bullish: Buy Call option. Max loss = Premium paid.',
    legs: [{ type: 'CE', action: 'BUY', strike: 0, lots: 1 }],
    getStrikes: (atm) => [atm]
  },
  'Long Put': {
    name: 'Long Put',
    description: 'Bearish: Buy Put option. Max loss = Premium paid.',
    legs: [{ type: 'PE', action: 'BUY', strike: 0, lots: 1 }],
    getStrikes: (atm) => [atm]
  },
  'Short Call': {
    name: 'Short Call',
    description: 'Bearish/Neutral: Sell Call option. Max profit = Premium received.',
    legs: [{ type: 'CE', action: 'SELL', strike: 0, lots: 1 }],
    getStrikes: (atm) => [atm]
  },
  'Short Put': {
    name: 'Short Put',
    description: 'Bullish/Neutral: Sell Put option. Max profit = Premium received.',
    legs: [{ type: 'PE', action: 'SELL', strike: 0, lots: 1 }],
    getStrikes: (atm) => [atm]
  },
  'Bull Call Spread': {
    name: 'Bull Call Spread',
    description: 'Moderately Bullish: Buy lower strike Call, Sell higher strike Call.',
    legs: [
      { type: 'CE', action: 'BUY', strike: 0, lots: 1 },
      { type: 'CE', action: 'SELL', strike: 0, lots: 1 }
    ],
    getStrikes: (atm, step) => [atm, atm + step * 2]
  },
  'Bear Put Spread': {
    name: 'Bear Put Spread',
    description: 'Moderately Bearish: Buy higher strike Put, Sell lower strike Put.',
    legs: [
      { type: 'PE', action: 'BUY', strike: 0, lots: 1 },
      { type: 'PE', action: 'SELL', strike: 0, lots: 1 }
    ],
    getStrikes: (atm, step) => [atm, atm - step * 2]
  },
  'Long Straddle': {
    name: 'Long Straddle',
    description: 'High volatility expected: Buy ATM Call and Put.',
    legs: [
      { type: 'CE', action: 'BUY', strike: 0, lots: 1 },
      { type: 'PE', action: 'BUY', strike: 0, lots: 1 }
    ],
    getStrikes: (atm) => [atm, atm]
  },
  'Short Straddle': {
    name: 'Short Straddle',
    description: 'Low volatility expected: Sell ATM Call and Put.',
    legs: [
      { type: 'CE', action: 'SELL', strike: 0, lots: 1 },
      { type: 'PE', action: 'SELL', strike: 0, lots: 1 }
    ],
    getStrikes: (atm) => [atm, atm]
  },
  'Long Strangle': {
    name: 'Long Strangle',
    description: 'High volatility expected: Buy OTM Call and Put.',
    legs: [
      { type: 'CE', action: 'BUY', strike: 0, lots: 1 },
      { type: 'PE', action: 'BUY', strike: 0, lots: 1 }
    ],
    getStrikes: (atm, step) => [atm + step * 2, atm - step * 2]
  },
  'Short Strangle': {
    name: 'Short Strangle',
    description: 'Low volatility expected: Sell OTM Call and Put.',
    legs: [
      { type: 'CE', action: 'SELL', strike: 0, lots: 1 },
      { type: 'PE', action: 'SELL', strike: 0, lots: 1 }
    ],
    getStrikes: (atm, step) => [atm + step * 2, atm - step * 2]
  },
  'Iron Condor': {
    name: 'Iron Condor',
    description: 'Neutral: Sell OTM Strangle, Buy further OTM options for protection.',
    legs: [
      { type: 'CE', action: 'SELL', strike: 0, lots: 1 },
      { type: 'CE', action: 'BUY', strike: 0, lots: 1 },
      { type: 'PE', action: 'SELL', strike: 0, lots: 1 },
      { type: 'PE', action: 'BUY', strike: 0, lots: 1 }
    ],
    getStrikes: (atm, step) => [atm + step * 2, atm + step * 4, atm - step * 2, atm - step * 4]
  },
  'Iron Butterfly': {
    name: 'Iron Butterfly',
    description: 'Neutral: Sell ATM Straddle, Buy OTM options for protection.',
    legs: [
      { type: 'CE', action: 'SELL', strike: 0, lots: 1 },
      { type: 'CE', action: 'BUY', strike: 0, lots: 1 },
      { type: 'PE', action: 'SELL', strike: 0, lots: 1 },
      { type: 'PE', action: 'BUY', strike: 0, lots: 1 }
    ],
    getStrikes: (atm, step) => [atm, atm + step * 3, atm, atm - step * 3]
  }
};

const StrategyBuilder: React.FC<Props> = ({
  optionChain,
  spotPrice,
  recommendedStrategy,
  lotSize = 50,
  oiLevels = [],
}) => {
  const [selectedStrategy, setSelectedStrategy] = useState<string>('');
  const [legs, setLegs] = useState<StrategyLeg[]>([]);
  const [showCustom, setShowCustom] = useState(false);

  // Get available strikes from option chain
  const availableStrikes = useMemo(() => {
    return optionChain.map(row => row.strike).sort((a, b) => a - b);
  }, [optionChain]);

  // Find ATM strike
  const atmStrike = useMemo(() => {
    if (availableStrikes.length === 0 || !spotPrice) return 0;
    return availableStrikes.reduce((prev, curr) =>
      Math.abs(curr - spotPrice) < Math.abs(prev - spotPrice) ? curr : prev
    );
  }, [availableStrikes, spotPrice]);

  // Calculate step size (difference between strikes)
  const stepSize = useMemo(() => {
    if (availableStrikes.length < 2) return 50;
    return availableStrikes[1] - availableStrikes[0];
  }, [availableStrikes]);

  // Get premium for a strike
  const getPremium = (strike: number, type: 'CE' | 'PE'): number => {
    const row = optionChain.find(r => r.strike === strike);
    if (!row) return 0;
    return type === 'CE' ? row.callLTP : row.putLTP;
  };

  // Find closest available strike
  const findClosestStrike = (targetStrike: number): number => {
    if (availableStrikes.length === 0) return targetStrike;
    return availableStrikes.reduce((prev, curr) =>
      Math.abs(curr - targetStrike) < Math.abs(prev - targetStrike) ? curr : prev
    );
  };

  // Initialize strategy based on recommended or selection
  useEffect(() => {
    if (recommendedStrategy && !selectedStrategy) {
      // Try to match recommended strategy to available strategies
      const matchedStrategy = Object.keys(STRATEGIES).find(s =>
        recommendedStrategy.toLowerCase().includes(s.toLowerCase()) ||
        s.toLowerCase().includes(recommendedStrategy.toLowerCase().split(' ')[0])
      );
      if (matchedStrategy) {
        setSelectedStrategy(matchedStrategy);
      }
    }
  }, [recommendedStrategy, selectedStrategy]);

  // Update legs when strategy changes
  useEffect(() => {
    if (!selectedStrategy || !STRATEGIES[selectedStrategy]) {
      setLegs([]);
      return;
    }

    const strategy = STRATEGIES[selectedStrategy];
    const strikes = strategy.getStrikes(atmStrike, stepSize);

    const newLegs: StrategyLeg[] = strategy.legs.map((leg, idx) => {
      const strike = findClosestStrike(strikes[idx] || atmStrike);
      return {
        id: `leg-${idx}`,
        type: leg.type,
        action: leg.action,
        strike,
        lots: leg.lots,
        premium: getPremium(strike, leg.type)
      };
    });

    setLegs(newLegs);
    setShowCustom(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStrategy, atmStrike, stepSize]);

  // Update leg
  const updateLeg = (id: string, updates: Partial<StrategyLeg>) => {
    setLegs(prevLegs =>
      prevLegs.map(leg => {
        if (leg.id !== id) return leg;
        const updatedLeg = { ...leg, ...updates };
        // Update premium if strike or type changed
        if (updates.strike !== undefined || updates.type !== undefined) {
          updatedLeg.premium = getPremium(updatedLeg.strike, updatedLeg.type);
        }
        return updatedLeg;
      })
    );
  };

  // Add custom leg
  const addLeg = () => {
    const newLeg: StrategyLeg = {
      id: `leg-${Date.now()}`,
      type: 'CE',
      action: 'BUY',
      strike: atmStrike,
      lots: 1,
      premium: getPremium(atmStrike, 'CE')
    };
    setLegs([...legs, newLeg]);
  };

  // Remove leg
  const removeLeg = (id: string) => {
    setLegs(legs.filter(leg => leg.id !== id));
  };

  // Calculate net premium
  const netPremium = useMemo(() => {
    return legs.reduce((total, leg) => {
      const legValue = leg.premium * leg.lots * lotSize;
      return leg.action === 'BUY' ? total - legValue : total + legValue;
    }, 0);
  }, [legs, lotSize]);

  // Calculate max profit and loss
  const { maxProfit, maxLoss, breakeven } = useMemo(() => {
    if (legs.length === 0) {
      return { maxProfit: 0, maxLoss: 0, breakeven: [] };
    }

    const buyLegs = legs.filter(l => l.action === 'BUY');
    const sellLegs = legs.filter(l => l.action === 'SELL');

    // Simplified P&L calculation for common strategies
    const totalBuyPremium = buyLegs.reduce((t, l) => t + l.premium * l.lots * lotSize, 0);
    const totalSellPremium = sellLegs.reduce((t, l) => t + l.premium * l.lots * lotSize, 0);

    let maxP = 0;
    let maxL = 0;
    let be: number[] = [];

    // Calculate based on strategy type
    if (buyLegs.length === 1 && sellLegs.length === 0) {
      // Long Call/Put
      maxL = totalBuyPremium;
      maxP = Infinity; // Unlimited for calls
      const leg = buyLegs[0];
      be = [leg.type === 'CE' ? leg.strike + leg.premium : leg.strike - leg.premium];
    } else if (sellLegs.length === 1 && buyLegs.length === 0) {
      // Short Call/Put
      maxP = totalSellPremium;
      maxL = Infinity; // Unlimited for naked short
      const leg = sellLegs[0];
      be = [leg.type === 'CE' ? leg.strike + leg.premium : leg.strike - leg.premium];
    } else if (buyLegs.length === 1 && sellLegs.length === 1 && buyLegs[0].type === sellLegs[0].type) {
      // Vertical Spread
      const width = Math.abs(buyLegs[0].strike - sellLegs[0].strike);
      const netPrem = totalSellPremium - totalBuyPremium;
      if (netPrem > 0) {
        // Credit spread
        maxP = netPrem;
        maxL = width * lotSize * buyLegs[0].lots - netPrem;
      } else {
        // Debit spread
        maxL = Math.abs(netPrem);
        maxP = width * lotSize * buyLegs[0].lots - maxL;
      }
      be = [buyLegs[0].type === 'CE'
        ? Math.min(buyLegs[0].strike, sellLegs[0].strike) + Math.abs(netPrem) / lotSize
        : Math.max(buyLegs[0].strike, sellLegs[0].strike) - Math.abs(netPrem) / lotSize
      ];
    } else if (legs.length === 2 && buyLegs.length === 2) {
      // Long Straddle/Strangle
      maxL = totalBuyPremium;
      maxP = Infinity;
      const ceLeg = legs.find(l => l.type === 'CE');
      const peLeg = legs.find(l => l.type === 'PE');
      if (ceLeg && peLeg) {
        const totalPrem = (ceLeg.premium + peLeg.premium);
        be = [ceLeg.strike + totalPrem, peLeg.strike - totalPrem];
      }
    } else if (legs.length === 2 && sellLegs.length === 2) {
      // Short Straddle/Strangle
      maxP = totalSellPremium;
      maxL = Infinity;
      const ceLeg = legs.find(l => l.type === 'CE');
      const peLeg = legs.find(l => l.type === 'PE');
      if (ceLeg && peLeg) {
        const totalPrem = (ceLeg.premium + peLeg.premium);
        be = [ceLeg.strike + totalPrem, peLeg.strike - totalPrem];
      }
    } else if (legs.length === 4) {
      // Iron Condor/Butterfly
      const ceLegs = legs.filter(l => l.type === 'CE').sort((a, b) => a.strike - b.strike);
      const peLegs = legs.filter(l => l.type === 'PE').sort((a, b) => b.strike - a.strike);

      if (ceLegs.length === 2 && peLegs.length === 2) {
        const netCredit = totalSellPremium - totalBuyPremium;
        if (netCredit > 0) {
          maxP = netCredit;
          const ceWidth = Math.abs(ceLegs[1].strike - ceLegs[0].strike);
          const peWidth = Math.abs(peLegs[0].strike - peLegs[1].strike);
          maxL = Math.max(ceWidth, peWidth) * lotSize - netCredit;
        } else {
          maxL = Math.abs(netCredit);
          const ceWidth = Math.abs(ceLegs[1].strike - ceLegs[0].strike);
          maxP = ceWidth * lotSize - maxL;
        }
        be = [
          ceLegs[0].strike + Math.abs(netCredit) / lotSize,
          peLegs[0].strike - Math.abs(netCredit) / lotSize
        ];
      }
    }

    return {
      maxProfit: maxP,
      maxLoss: maxL,
      breakeven: be.filter(b => b > 0).map(b => Math.round(b))
    };
  }, [legs, lotSize]);

  if (optionChain.length === 0) {
    return (
      <div className="has-text-centered py-2">
        <p className="is-size-7 has-text-grey">Load option chain data to use Strategy Builder</p>
      </div>
    );
  }

  return (
    <div>
      {/* Custom Strategy Button */}
      <div className="level mb-2">
        <div className="level-left">
          <span className="is-size-7 has-text-grey">Build or select a strategy</span>
        </div>
        <div className="level-right">
          <button
            onClick={() => {
              setShowCustom(true);
              setSelectedStrategy('');
              setLegs([]);
            }}
            className="button is-small is-info is-outlined"
          >
            <span className="icon is-small"><i className="fa fa-plus"></i></span>
            <span>Custom</span>
          </button>
        </div>
      </div>

      {/* OI-Based Strike Suggestions */}
      {oiLevels.length > 0 && (
        <div style={{ backgroundColor: 'rgba(41,98,255,0.08)', borderRadius: '4px', padding: '0.5rem', marginBottom: '0.5rem', fontSize: '0.7rem' }}>
          <strong style={{ fontSize: '0.7rem' }}>OI Smart Strikes:</strong>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.25rem', flexWrap: 'wrap' }}>
            {oiLevels.filter(l => l.type === 'oi_resistance').map((l, i) => (
              <span key={i} style={{ color: '#ef5350' }}>
                Sell CE @ {l.strike} ({(l.oi / 100000).toFixed(1)}L OI)
              </span>
            ))}
            {oiLevels.filter(l => l.type === 'oi_support').map((l, i) => (
              <span key={i} style={{ color: '#26a69a' }}>
                Sell PE @ {l.strike} ({(l.oi / 100000).toFixed(1)}L OI)
              </span>
            ))}
          </div>
          <p style={{ color: '#787b86', fontSize: '0.6rem', marginTop: '0.25rem' }}>
            High OI = writers confident price won't breach. Ideal for selling options.
          </p>
        </div>
      )}

      {/* Strategy Selection */}
      <div className="field mb-2">
        <label className="label is-small mb-1">Select Strategy</label>
        <div className="control">
          <div className="select is-small is-fullwidth">
            <select
              value={selectedStrategy}
              onChange={(e) => setSelectedStrategy(e.target.value)}
            >
              <option value="">-- Select Strategy --</option>
              <optgroup label="Single Leg">
                <option value="Long Call">Long Call (Bullish)</option>
                <option value="Long Put">Long Put (Bearish)</option>
                <option value="Short Call">Short Call (Bearish/Neutral)</option>
                <option value="Short Put">Short Put (Bullish/Neutral)</option>
              </optgroup>
              <optgroup label="Spreads">
                <option value="Bull Call Spread">Bull Call Spread</option>
                <option value="Bear Put Spread">Bear Put Spread</option>
              </optgroup>
              <optgroup label="Volatility">
                <option value="Long Straddle">Long Straddle</option>
                <option value="Short Straddle">Short Straddle</option>
                <option value="Long Strangle">Long Strangle</option>
                <option value="Short Strangle">Short Strangle</option>
              </optgroup>
              <optgroup label="Advanced">
                <option value="Iron Condor">Iron Condor</option>
                <option value="Iron Butterfly">Iron Butterfly</option>
              </optgroup>
            </select>
          </div>
        </div>
        {selectedStrategy && STRATEGIES[selectedStrategy] && (
          <p className="help is-info">{STRATEGIES[selectedStrategy].description}</p>
        )}
      </div>

      {recommendedStrategy && (
        <div className="notification is-info is-light py-2 px-3 mb-2">
          <p className="is-size-7">
            <strong>AI Recommended:</strong> {recommendedStrategy}
          </p>
        </div>
      )}

      {/* Strategy Legs */}
      {legs.length > 0 && (
        <div className="mb-3">
          <div className="level mb-1">
            <div className="level-left">
              <label className="label is-small mb-0">Strategy Legs</label>
            </div>
            {showCustom && (
              <div className="level-right">
                <button onClick={addLeg} className="button is-small is-success is-outlined">
                  <span className="icon is-small"><i className="fa fa-plus"></i></span>
                  <span>Add Leg</span>
                </button>
              </div>
            )}
          </div>

          <div className="table-container">
            <table className="table is-fullwidth is-narrow is-hoverable" style={{ fontSize: '0.75rem' }}>
              <thead>
                <tr>
                  <th className="py-1">Action</th>
                  <th className="py-1">Type</th>
                  <th className="py-1">Strike</th>
                  <th className="py-1">Lots</th>
                  <th className="py-1">Premium</th>
                  <th className="py-1">Value</th>
                  {showCustom && <th className="py-1"></th>}
                </tr>
              </thead>
              <tbody>
                {legs.map((leg) => (
                  <tr key={leg.id}>
                    <td className="py-1">
                      <div className="select is-small">
                        <select
                          value={leg.action}
                          onChange={(e) => updateLeg(leg.id, { action: e.target.value as 'BUY' | 'SELL' })}
                          className={leg.action === 'BUY' ? 'has-text-success' : 'has-text-danger'}
                        >
                          <option value="BUY">BUY</option>
                          <option value="SELL">SELL</option>
                        </select>
                      </div>
                    </td>
                    <td className="py-1">
                      <div className="select is-small">
                        <select
                          value={leg.type}
                          onChange={(e) => updateLeg(leg.id, { type: e.target.value as 'CE' | 'PE' })}
                        >
                          <option value="CE">CALL</option>
                          <option value="PE">PUT</option>
                        </select>
                      </div>
                    </td>
                    <td className="py-1">
                      <div className="select is-small">
                        <select
                          value={leg.strike}
                          onChange={(e) => updateLeg(leg.id, { strike: Number(e.target.value) })}
                        >
                          {availableStrikes.map(s => (
                            <option key={s} value={s}>
                              {s} {s === atmStrike ? '(ATM)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>
                    <td className="py-1">
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={leg.lots}
                        onChange={(e) => updateLeg(leg.id, { lots: Number(e.target.value) })}
                        className="input is-small"
                        style={{ width: '60px' }}
                      />
                    </td>
                    <td className="py-1 has-text-weight-bold">
                      {leg.premium.toFixed(2)}
                    </td>
                    <td className={`py-1 has-text-weight-bold ${leg.action === 'BUY' ? 'has-text-danger' : 'has-text-success'}`}>
                      {leg.action === 'BUY' ? '-' : '+'}
                      {(leg.premium * leg.lots * lotSize).toFixed(0)}
                    </td>
                    {showCustom && (
                      <td className="py-1">
                        <button
                          onClick={() => removeLeg(leg.id)}
                          className="button is-small is-danger is-outlined"
                        >
                          <span className="icon is-small"><i className="fa fa-trash"></i></span>
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCustom && legs.length === 0 && (
        <div className="notification is-light has-text-centered py-3 mb-3">
          <p className="is-size-7">Click "Add Leg" to start building your custom strategy</p>
          <button onClick={addLeg} className="button is-small is-success mt-2">
            <span className="icon is-small"><i className="fa fa-plus"></i></span>
            <span>Add First Leg</span>
          </button>
        </div>
      )}

      {/* P&L Summary */}
      {legs.length > 0 && (
        <div className="columns is-multiline mb-0" style={{ margin: '0 -0.25rem' }}>
          <div className="column is-4 py-1" style={{ padding: '0 0.25rem' }}>
            <div className={`box py-2 px-3 mb-0 ${netPremium >= 0 ? 'has-background-success-light' : 'has-background-danger-light'}`}>
              <p className="heading is-size-7 mb-0">Net Premium</p>
              <p className={`title is-6 mb-0 ${netPremium >= 0 ? 'has-text-success' : 'has-text-danger'}`}>
                {netPremium >= 0 ? '+' : ''}{netPremium.toFixed(0)}
              </p>
              <p className="is-size-7 has-text-grey">{netPremium >= 0 ? 'Credit' : 'Debit'}</p>
            </div>
          </div>

          <div className="column is-4 py-1" style={{ padding: '0 0.25rem' }}>
            <div className="box has-background-success-light py-2 px-3 mb-0">
              <p className="heading is-size-7 mb-0">Max Profit</p>
              <p className="title is-6 mb-0 has-text-success">
                {maxProfit === Infinity ? 'Unlimited' : `+${maxProfit.toFixed(0)}`}
              </p>
            </div>
          </div>

          <div className="column is-4 py-1" style={{ padding: '0 0.25rem' }}>
            <div className="box has-background-danger-light py-2 px-3 mb-0">
              <p className="heading is-size-7 mb-0">Max Loss</p>
              <p className="title is-6 mb-0 has-text-danger">
                {maxLoss === Infinity ? 'Unlimited' : `-${maxLoss.toFixed(0)}`}
              </p>
            </div>
          </div>

          {breakeven.length > 0 && (
            <div className="column is-12 py-1" style={{ padding: '0 0.25rem' }}>
              <div className="box has-background-warning-light py-2 px-3 mb-0">
                <p className="heading is-size-7 mb-0">Breakeven Point(s)</p>
                <p className="title is-6 mb-0">
                  {breakeven.map((be, idx) => (
                    <span key={idx} className="tag is-warning mr-1">{be}</span>
                  ))}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info */}
      <div className="notification is-light py-2 px-3 mt-2 mb-0">
        <p className="is-size-7 mb-0">
          <strong>Lot Size:</strong> {lotSize} | <strong>ATM:</strong> {atmStrike} | <strong>Spot:</strong> {spotPrice.toFixed(2)}
        </p>
      </div>
    </div>
  );
};

export default StrategyBuilder;
